import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  type Api,
  ApiError,
  type Bootstrap,
  PLATFORM_TENANT,
  type Project,
  type SessionUser,
  type Tenant,
  apis,
  getBootstrapAudiences,
  getProjects,
  getSession,
  getTenants,
  requestToken,
  signOut,
} from "../data/api";

// ---
// What the console has loaded
//
// Routes say where you are; this says what is known. They are separate because the two change for
// different reasons: a tenant's token and projects survive navigating between its pages, and
// re-minting them on every route change would make the console slower and the server busier for
// no gain.
//
// It is a context rather than router context because router context is given once, at creation,
// and this is replaced as sessions start, tenants are chosen and tokens are minted.
// ---

export interface ConsoleState {
  readonly user: SessionUser | null;
  readonly loading: boolean;
  readonly bootstrap: Bootstrap | null;
  readonly tenants: readonly Tenant[];
  /** Whether the tenant list has been fetched. Empty-and-unknown is not empty-and-known. */
  readonly tenantsLoaded: boolean;
  /**
   * Whether the subject holds a role in the platform tenant, which the console requires. Null
   * until the tenant list has arrived, and left null if fetching it failed: an outage must not
   * read as "not staff" and sign an administrator out.
   */
  readonly isStaff: boolean | null;
  readonly projects: readonly Project[];
  /** This tenant's registered APIs, with the dimensions each requires. */
  readonly registeredApis: readonly Api[];
  readonly controlToken: string | null;
  readonly controlScopes: readonly string[];
  /**
   * Whether the attempt to mint a control-plane token has finished, either way.
   *
   * The same distinction `tenantsLoaded` draws: no scopes yet and no scopes at all look identical
   * from outside, and acting on the first sends a caller to a page they cannot open.
   */
  readonly controlLoaded: boolean;
  readonly platformToken: string | null;
  readonly error: string | null;
  /** The tenant the current route names, so loading follows the URL rather than a click. */
  readonly tenant: string;
  setTenant(tenant: string): void;
  refreshSession(): Promise<void>;
  signOutEverywhere(): void;
  setError(message: string | null): void;
}

const Context = createContext<ConsoleState | null>(null);

/** The console's loaded state. Throws outside the provider, which is a wiring mistake. */
export function useConsole(): ConsoleState {
  const state = useContext(Context);
  if (state === null) throw new Error("useConsole outside ConsoleProvider");
  return state;
}

export function ConsoleProvider({
  tenant,
  children,
}: {
  /** From the route. The provider follows it; it does not choose it. */
  tenant: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);
  const [isStaff, setIsStaff] = useState<boolean | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [registeredApis, setRegisteredApis] = useState<Api[]>([]);
  const [controlToken, setControlToken] = useState<string | null>(null);
  const [controlLoaded, setControlLoaded] = useState(false);
  const [controlScopes, setControlScopes] = useState<string[]>([]);
  const [platformToken, setPlatformToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState("");

  const refreshSession = useCallback(async (): Promise<void> => {
    const session = await getSession();
    setUser(session?.user ?? null);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  // Before any token: which audiences to name when asking for one.
  useEffect(() => {
    void getBootstrapAudiences()
      .then(setBootstrap)
      .catch((caught: unknown) =>
        setError(caught instanceof ApiError ? `${caught.code}: ${caught.message}` : String(caught)),
      );
  }, []);

  useEffect(() => {
    if (user === null || bootstrap === null) return;
    void requestToken({ resource: bootstrap.platform, tenantId: PLATFORM_TENANT })
      .then((token) => setPlatformToken(token.access_token))
      // Not an error: almost nobody holds platform authority.
      .catch(() => setPlatformToken(null));
  }, [user, bootstrap]);

  useEffect(() => {
    if (user === null) return;
    void getTenants()
      .then((list) => {
        setTenants(list);
        setIsStaff(list.some((each) => each.id === PLATFORM_TENANT));
      })
      .catch((caught: unknown) => setError(String(caught)))
      .finally(() => setTenantsLoaded(true));
  }, [user]);

  // The route names the tenant. Anything it does not — the first sign-in, `/tokens` — falls back
  // to the first one the subject belongs to.
  const active = tenant !== "" ? tenant : chosen !== "" ? chosen : (tenants[0]?.id ?? "");

  useEffect(() => {
    if (user === null || active === "" || bootstrap === null) return;
    setProjects([]);
    setControlScopes([]);
    setControlLoaded(false);
    let cancelled = false;
    void (async () => {
      try {
        const control = await requestToken({ resource: bootstrap.controlPlane, tenantId: active });
        if (cancelled) return;
        // Held, not used once: every administration screen runs on this token.
        setControlToken(control.access_token);
        setControlScopes(control.scope === "" ? [] : control.scope.split(" ").sort());
        setControlLoaded(true);

        const list = await getProjects(active, control.access_token);
        if (cancelled) return;
        setProjects(list);

        // This tenant's own APIs, so forms offer what exists here. Needs `permission:read`;
        // without it the control plane alone remains, which everyone can reach.
        const registered = await apis.list(active, control.access_token).catch((): Api[] => []);
        if (cancelled) return;
        setRegisteredApis(registered);
      } catch (caught) {
        if (cancelled) return;
        setProjects([]);
        setControlToken(null);
        setControlScopes([]);
        // Loaded, and the answer is none. A caller whose roles are all project-scoped resolves
        // into no control-plane token, which is a settled state rather than one still arriving.
        setControlLoaded(true);
        setError(caught instanceof ApiError ? `${caught.code}: ${caught.message}` : String(caught));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, active, bootstrap]);

  function signOutEverywhere(): void {
    void signOut().then(() => {
      setTenants([]);
      setTenantsLoaded(false);
      setIsStaff(null);
      setChosen("");
      setControlToken(null);
      setControlScopes([]);
      setControlLoaded(false);
      setPlatformToken(null);
      return refreshSession();
    });
  }

  return (
    <Context.Provider
      value={{
        user,
        loading,
        bootstrap,
        tenants,
        tenantsLoaded,
        isStaff,
        projects,
        registeredApis,
        controlToken,
        controlLoaded,
        controlScopes,
        platformToken,
        error,
        tenant: active,
        setTenant: setChosen,
        refreshSession,
        signOutEverywhere,
        setError,
      }}
    >
      {children}
    </Context.Provider>
  );
}
